import React, { useState, useRef } from 'react';
import { X, Upload, FileSpreadsheet, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { productsApi } from '../../../services/api';
import type { BulkImportResponse } from '../../../types';

interface ProductExcelImportProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const ProductExcelImport: React.FC<ProductExcelImportProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<BulkImportResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setError(null);
    setResult(null);

    // Validate extension
    if (!selectedFile.name.endsWith('.xlsx')) {
      setError('El archivo debe ser un Excel (.xlsx)');
      return;
    }

    // Validate size (10MB)
    if (selectedFile.size > 10 * 1024 * 1024) {
      setError('El archivo excede el tamaño máximo de 10MB');
      return;
    }

    setFile(selectedFile);
  };

  const handleImport = async () => {
    if (!file) return;

    setImporting(true);
    setError(null);
    setResult(null);

    try {
      const response = await productsApi.importFromExcel(file);
      setResult(response);
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al importar productos');
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setResult(null);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-green-600 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileSpreadsheet size={24} />
            <div>
              <h2 className="text-xl font-bold">Importar Productos</h2>
              <p className="text-sm text-green-100">Desde archivo Excel</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-green-700 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-start gap-3">
              <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
              <div className="flex-1">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          )}

          {!result ? (
            <>
              {/* Upload Section */}
              <div className="max-w-xl mx-auto">
                <div className="text-center mb-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Selecciona un archivo Excel</h3>
                  <p className="text-sm text-slate-600">Formato soportado: .xlsx</p>
                </div>

                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-slate-300 rounded-lg p-12 text-center cursor-pointer hover:border-green-500 hover:bg-green-50 transition-colors"
                >
                  <Upload className="mx-auto mb-4 text-slate-400" size={48} />
                  <p className="text-slate-700 font-medium mb-1">
                    {file ? file.name : 'Haz clic para seleccionar un archivo'}
                  </p>
                  <p className="text-sm text-slate-500">o arrastra y suelta aquí</p>
                  {file && (
                    <p className="text-xs text-slate-500 mt-2">
                      Tamaño: {(file.size / 1024).toFixed(2)} KB
                    </p>
                  )}
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx"
                  onChange={handleFileChange}
                  className="hidden"
                />

                <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800 font-medium mb-2">Formato del archivo:</p>
                  <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                    <li>La fila 17 debe contener los nombres de las columnas</li>
                    <li>Las filas 18+ contienen los datos de productos</li>
                    <li>El backend procesará automáticamente todas las columnas</li>
                  </ul>
                </div>
              </div>
            </>
          ) : (
            /* Results Section */
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-6">
                <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${
                  result.failed === 0 ? 'bg-green-100' : 'bg-yellow-100'
                }`}>
                  {result.failed === 0 ? (
                    <CheckCircle className="text-green-600" size={32} />
                  ) : (
                    <AlertCircle className="text-yellow-600" size={32} />
                  )}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">
                  {result.failed === 0 ? '¡Importación Completada!' : 'Importación Completada con Errores'}
                </h3>
                <p className="text-slate-600">
                  {result.failed === 0 
                    ? 'Todos los registros se procesaron correctamente'
                    : `${result.totalItems - result.failed} de ${result.totalItems} registros procesados correctamente`
                  }
                </p>
              </div>

              {/* Statistics */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-blue-700">{result.created}</div>
                  <div className="text-sm text-blue-600">Creados</div>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-green-700">{result.updated}</div>
                  <div className="text-sm text-green-600">Actualizados</div>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-yellow-700">{result.skipped}</div>
                  <div className="text-sm text-yellow-600">Omitidos</div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-red-700">{result.failed}</div>
                  <div className="text-sm text-red-600">Fallidos</div>
                </div>
              </div>

              {/* Errors */}
              {result.errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <XCircle className="text-red-600" size={20} />
                    <h4 className="font-semibold text-red-900">Errores Encontrados</h4>
                  </div>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {result.errors.map((err, idx) => (
                      <div key={idx} className="text-sm text-red-800 bg-white rounded p-2">
                        <span className="font-medium">Fila {err.index + 18}</span>
                        {err.externalId && <span className="text-red-600"> ({err.externalId})</span>}
                        : {err.message}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-slate-50 px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-3">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-slate-700 hover:bg-slate-200 rounded-lg transition-colors font-medium"
          >
            {result ? 'Cerrar' : 'Cancelar'}
          </button>
          {!result && file && (
            <button
              onClick={handleImport}
              disabled={importing}
              className={`
                flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-white font-medium transition-all duration-300
                ${importing
                  ? 'bg-green-400 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700'
                }
              `}
            >
              {importing && (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              )}
              {importing ? 'Importando...' : 'Importar'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
